<template>
    <div class="rcv-filterbar">
        <!-- Tìm kiếm từ khoá -->
        <el-input
            v-model="local.keyword"
            placeholder="Tìm theo tên, email, SĐT..."
            prefix-icon="el-icon-search"
            size="small"
            clearable
            class="rcv-filterbar__keyword"
            @input="emitDebounced"
            @clear="emitChange"
        />


        <!-- Lọc theo Tiến trình / Trạng thái -->
        <el-select
            v-model="local.status"
            placeholder="Tiến trình ứng viên"
            size="small"
            clearable
            filterable
            class="rcv-filterbar__status"
            @change="emitChange"
        >
            <el-option :value="''" label="Tất cả tiến trình" />
            <el-option
                v-for="opt in statusOptions"
                :key="opt.value"
                :label="`[#${opt.value}] ${opt.label}`"
                :value="opt.value"
            >
                <span class="rcv-status-dot" :style="{ backgroundColor: opt.color }"></span>
                <span class="rcv-status-opt-label">{{ opt.label }}</span>
                <span class="rcv-status-opt-id">#{{ opt.value }}</span>
            </el-option>
        </el-select>

        <!-- Lọc theo Team Lead (Chỉ cho department_id = 3 mới được dùng) -->
        <el-select
            v-if="canFilterTeamlead"
            v-model="local.teamlead_id"
            placeholder="Team Lead"
            size="small"
            clearable
            filterable
            class="rcv-filterbar__tl"
            @change="emitChange"
        >
            <el-option :value="''" label="Tất cả Team Lead" />
            <el-option
                v-for="tl in teamLeads"
                :key="tl.id"
                :label="tl.name || tl.full_name || `TL #${tl.id}`"
                :value="tl.id"
            >
                <span>{{ tl.name || tl.full_name || `TL #${tl.id}` }}</span>
                <span v-if="tl.department_name || tl.team_name" class="rcv-opt-sub">
                    {{ tl.department_name || tl.team_name }}
                </span>
            </el-option>
        </el-select>

        <!-- Lọc theo Khoảng ngày nộp hồ sơ -->
        <el-date-picker
            v-model="local.date_range"
            type="daterange"
            size="small"
            range-separator="—"
            start-placeholder="Từ ngày"
            end-placeholder="Đến ngày"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            class="rcv-filterbar__date"
            @change="emitChange"
        />

        <el-button
            size="small"
            icon="el-icon-refresh"
            circle
            class="rcv-filterbar__reset"
            title="Xoá bộ lọc"
            @click="reset"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import { STATUS_OPTIONS } from "@/core/config/recruitmentCvs/recruitmentCvsOption";
import { RO_GET_TEACHER_GROUPS } from "@/core/services/store/recruitment-orders/recruitmentOrders.module";

const BLANK_FILTERS = {
    keyword: "",
    job_id: "",
    type: "",
    status: "",
    person_in_charge_id: "",
    teamlead_id: "",
    date_range: null,
};

export default {
    name: "CvFilterBar",
    props: {
        value: { type: Object, default: () => ({ ...BLANK_FILTERS }) },
        orders: { type: Array, default: () => [] },
    },
    data() {
        return {
            local: { ...BLANK_FILTERS, ...this.value },
            hrUsers: [],
            teamLeads: [],
            debounceTimer: null,
        };
    },
    computed: {
        ...mapGetters(["currentUser"]),
        currentDepartmentId() {
            const u = this.currentUser || {};
            if (u.department_id) return Number(u.department_id);
            if (u.profile && u.profile.department_id) return Number(u.profile.department_id);
            if (u.department && u.department.id) return Number(u.department.id);
            if (Array.isArray(u.use_department) && u.use_department.length > 0) {
                return Number(u.use_department[0].department_id || u.use_department[0].id || 0);
            }
            if (u.use_department && typeof u.use_department === "object") {
                return Number(u.use_department.department_id || u.use_department.id || 0);
            }
            return null;
        },
        canFilterTeamlead() {
            return Number(this.currentDepartmentId) === 3;
        },
        statusOptions() {
            return STATUS_OPTIONS;
        },
    },
    watch: {
        value: {
            deep: true,
            handler(val) {
                this.local = { ...BLANK_FILTERS, ...val };
            },
        },
    },
    mounted() {
        this.loadFiltersData();
    },
    beforeDestroy() {
        clearTimeout(this.debounceTimer);
    },
    methods: {
        loadFiltersData() {
            // Tải danh sách HR (Dept 5)
            ApiService.query("user/filter", { department_id: 5 })
                .then(({ data }) => {
                    const list = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
                    this.hrUsers = list;
                })
                .catch(() => {
                    ApiService.query("/list-user", { department_id: 5, limit: 100 })
                        .then(({ data }) => {
                            const list = (data && data.data && (data.data.data || data.data)) || [];
                            this.hrUsers = Array.isArray(list) ? list : [];
                        })
                        .catch(() => {});
                });

            // Tải danh sách Team Lead nếu user có department_id = 3
            if (this.canFilterTeamlead) {
                this.$store
                    .dispatch(RO_GET_TEACHER_GROUPS, {})
                    .then((list) => {
                        this.teamLeads = Array.isArray(list) ? list : [];
                    })
                    .catch(() => {});
            }
        },
        orderLabel(order) {
            if (!order) return "";
            const title = order.title || order.position_title || "Yêu cầu tuyển dụng";
            return `#${order.id} - ${title}`;
        },
        emitChange() {
            const startDate = this.local.date_range && this.local.date_range[0];
            const endDate = this.local.date_range && this.local.date_range[1];
            const payload = {
                ...this.local,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            };
            this.$emit("input", payload);
            this.$emit("change", payload);
        },
        emitDebounced() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(this.emitChange, 350);
        },
        reset() {
            this.local = { ...BLANK_FILTERS };
            this.emitChange();
        },
    },
};
</script>

<style scoped>
.rcv-filterbar {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}
.rcv-filterbar__keyword {
    width: 220px;
}
.rcv-filterbar__job {
    width: 200px;
}
.rcv-filterbar__status {
    width: 200px;
}
.rcv-filterbar__hr {
    width: 170px;
}
.rcv-filterbar__tl {
    width: 170px;
}
.rcv-filterbar__date {
    width: 220px;
}
.rcv-opt-id {
    font-weight: 700;
    color: #4f46e5;
    margin-right: 6px;
}
.rcv-opt-sub {
    float: right;
    color: #94a3b8;
    font-size: 11px;
    margin-left: 8px;
}
.rcv-status-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    vertical-align: middle;
}
.rcv-status-opt-label {
    vertical-align: middle;
}
.rcv-status-opt-id {
    float: right;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 600;
}
.rcv-filterbar__reset {
    margin-left: 2px;
}
</style>
